import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import CheckGroup, {
  CheckGroupContainer,
  CheckOption,
} from '@/components/ui/custom/dataInput/CheckGroup';
import NotificationTag from '@/components/notifications/card/NotificationTag';
import useAuth from '@/data/hook/useAuth';
import {
  klassesQueryKeys,
  unitsQueryKeys,
  usersQueryKeys,
} from '@/data/services/querykeys';
import { isSuperAdmin, isTeacher } from '@/functions/auth';
import { handleUserFullName } from '@/functions/handleUserFullName';
import { AlertTypeEnum } from '@/models/Alert';
import { UserStatusEnum, UserTypeEnum } from '@/models/User';
import { getAuthorizedUnits } from '@/utils/getAuthorizedUnits';
import {
  CreateFilter,
  createFilterHandlers,
} from '@/components/filter/CreateFilter';

import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { AlertFiltersOptions } from './NotificationsPageContent';

const NotificationFiltersForm = () => {
  const { t } = useTranslation('translation', { keyPrefix: 'alerts.filters' });
  const { user } = useAuth();
  const { register, watch, setValue } = useFormContext<AlertFiltersOptions>();

  const [unitId, klassId, types, teacherId] = watch([
    'unitId',
    'klassId',
    'types',
    'teacherId',
  ]);

  const { handleClean, handleTypeChange } =
    createFilterHandlers<AlertFiltersOptions>(setValue);

  const alertTypeOptions: CheckOption[] = Object.values(AlertTypeEnum).map(
    type => ({
      id: 'checkbox' + type,
      value: type,
      label: <NotificationTag type={type} />,
    }),
  );

  return (
    <section className="flex flex-col gap-4">
      <CheckGroupContainer
        title={t('type')}
        badgeCount={types?.length}
        initOpen
      >
        <CheckGroup options={alertTypeOptions} {...register('types')} />
      </CheckGroupContainer>
      <ConditionalRenderer
        condition={
          (user?.unitsIds.length || 0) > 1 || isSuperAdmin(user?.userType)
        }
      >
        <CreateFilter
          title={t('unit')}
          badgeCount={unitId?.length}
          selecteds={unitId}
          onClean={() => handleClean('unitId')}
          service={unitsQueryKeys.list}
          filters={{
            idIn: getAuthorizedUnits(user)?.toString(),
            isActive: true,
          }}
          showSearch={isSuperAdmin(user?.userType)}
          displayName={unit => unit.name}
          {...register('unitId', {
            onChange: e => handleTypeChange(e, 'unitId', unitId),
          })}
        />
      </ConditionalRenderer>
      <ConditionalRenderer condition={!isTeacher(user?.userType)}>
        <CreateFilter
          title={t('teacher')}
          badgeCount={teacherId?.length}
          onClean={() => handleClean('teacherId')}
          service={usersQueryKeys.list}
          showSearch
          selecteds={teacherId}
          filters={{
            userType: [UserTypeEnum.TEACHER],
            unitId: getAuthorizedUnits(user),
            status: [UserStatusEnum.REGISTRED],
          }}
          displayName={handleUserFullName}
          {...register('teacherId', {
            onChange: e => handleTypeChange(e, 'teacherId', teacherId),
          })}
        />
      </ConditionalRenderer>

      <CreateFilter
        title={t('klass')}
        badgeCount={klassId?.length}
        onClean={() => handleClean('klassId')}
        service={klassesQueryKeys.list}
        showSearch
        selecteds={klassId}
        filters={{
          unitId: getAuthorizedUnits(user),
        }}
        displayName={klass => klass.name}
        {...(register('klassId'),
        {
          onChange: e => handleTypeChange(e, 'klassId', klassId),
        })}
      />
    </section>
  );
};

export default NotificationFiltersForm;
